import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TombamentoService } from 'administrativo-lib';
import { Login, FuncaoService, Relatorio, Tombamento, GlobalService, Pessoa, SetorService } from 'eddydata-lib';
import { MessageService } from 'primeng/api';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface Tipo {
  label: string;
  valor: 'COMPRA' | 'DOACAO';
  id: string;
}

@Component({
  selector: 'app-incorporacao-aquisicao-rpt',
  templateUrl: './incorporacao-aquisicao-rpt.component.html',
  styleUrls: ['./incorporacao-aquisicao-rpt.component.css']
})
export class IncorporacaoAquisicaoRptComponent implements OnInit {

  @Input() login: Login;
  @Input() visualizar: boolean = false;
  @Output() visualizarChange: EventEmitter<boolean> = new EventEmitter();

  public periodoIncorporacao: Date[] = null;
  public data_inicio: Date = null;
  public data_fim: Date = null;
  public responsavel: Pessoa = null;
  public tipos: Tipo[] = [];
  public type: Tipo['valor'] = 'COMPRA';
  protected unsubscribe: Subject<void> = new Subject();

  constructor(
    protected funcaoService: FuncaoService,
    protected tombamentoService: TombamentoService,
    private messageService: MessageService,
    private setorService: SetorService) {
  }

  ngOnInit() {
    this.tipos = [
      {
        id: 'CO',
        label: 'Compra',
        valor: 'COMPRA'
      },
      {
        id: 'DO',
        label: 'Doação',
        valor: 'DOACAO'
      },
    ];

    this.type = 'COMPRA';

    // A regra correta é pegar o setor logado, porém na data que foi feita esse chamado estava com um erro no objeto de usuário do login que
    // ao mudar de setor, o setor principal do usuário logado que era alterado, não o setor de fora do cadastro do usuário. 
    // Devido a regra estar em teoria "correta", o código foi deixado assim devido a não podermos ajustar o login no momento devido a demanda
    // do edital. Se algum dia o login for corrigido e o setor logado ficar fora do cadastro do usuário no patrimônio, é necessário mudar a regra abaixo.
    this.setorService.obter({
      id: this.login.usuario?.setor?.id,
      relations: 'responsavel'
    })
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(setor => {
        this.responsavel = setor.responsavel;
      });
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  protected afterInit(): void {
  }

  public onHide() {
    this.visualizar = false;
    this.visualizarChange.emit(this.visualizar);
  }

  atualizarMascara() {
    new GlobalService().calendarMascara();
  }

  private antesImprimir(): void {
    try {
      if (!this.data_inicio) {
        throw new Error('Preencha o período inicial');
      }
      if (!this.data_fim) {
        throw new Error('Preencha o período final');
      }
    } catch (e) {
      this.messageService.add({ key: 'tl', severity: 'warn', summary: 'Validação', detail: e.message });
      throw e;
    }
  }

  public async imprimir() {
    this.antesImprimir();

    const parametros = {
      orgao_id: this.login.orgao.id,
      data_cadastro$bt: `${this.funcaoService.converteDataSQL(this.data_inicio)},${this.funcaoService.converteDataSQL(this.data_fim)}`,
      aquisicao: this.type,
      relations: 'produto,empenho.compra,empenho.subelemento'
    };

    const subtitulo = `${this.funcaoService.converteDataBR(this.data_inicio)} a ${this.funcaoService.converteDataBR(this.data_fim)} - ${this.tipos.find(t => t.valor === this.type)?.label}`
    this.tombamentoService.filtrar(1, -1, parametros)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(lista => {
        if (!lista.content.length) {
          this.messageService.add({ key: 'tl', severity: 'warn', summary: 'Aviso', detail: 'Dados não encontrados!' });
          throw new Error(`Dados não encontrados!`)
        }
        Relatorio.imprimirPersonalizado('LISTAGEM DE INCORPORAÇÃO POR AQUISIÇÃO',
          this.login.usuario.nome,
          this.login.usuario.sobrenome,
          this.login.orgao.nome,
          this.login.brasao,
          this.montarConteudo(lista.content),
          'landscape',
          'LISTAGEM DE INCORPORAÇÃO POR AQUISIÇÃO',
          {
            linhas: {
              hLineWidth() {
                return 1;
              },
              vLineWidth() {
                return 1;
              },
              hLineColor() {
                return 'black';
              },
              paddingLeft() {
                return 3;
              },
              paddingRight() {
                return 3;
              }
            }
          }, false, false, 'pdf', subtitulo);
      });
  }

  private montarConteudo(lista: Tombamento[]) {
    const cabecalho = [];
    const conteudo = [];
    const assinatura = [];

    cabecalho.push([
      { text: 'Placa', fontSize: 8, alignment: 'center', bold: true, border: [true, true, true, true] },
      { text: 'Produto', fontSize: 8, alignment: 'center', bold: true, border: [true, true, true, true] },
      { text: 'OF.', fontSize: 8, alignment: 'center', bold: true, border: [true, true, true, true] },
      { text: 'Situação', fontSize: 8, alignment: 'center', bold: true, border: [true, true, true, true] },
      { text: 'Classificação da Despesa', fontSize: 8, alignment: 'center', bold: true, border: [true, true, true, true] },
      { text: 'Data de Incorporação', fontSize: 8, alignment: 'center', bold: true, border: [true, true, true, true] },
      { text: 'Data de Aquisição', fontSize: 8, alignment: 'center', bold: true, border: [true, true, true, true] },
      { text: 'Valor de Aquisição', fontSize: 8, alignment: 'center', bold: true, border: [true, true, true, true] },
      { text: 'Valor Atual', fontSize: 8, alignment: 'center', bold: true, border: [true, true, true, true] },
    ]);

    let valor_total: number = 0;
    let total_itens: number = 0;
    for (const item of lista) {
      valor_total += +item.valor_atual;
      total_itens++;
      cabecalho.push([
        { text: item.placa, fontSize: 8, alignment: 'center', border: [true, false, true, true] },
        { text: `${item.produto?.codigo} - ${item.produto?.nome}`, fontSize: 8, alignment: 'center', border: [true, false, true, true] },
        { text: item.empenho?.compra?.numero, fontSize: 8, alignment: 'center', border: [true, false, true, true] },
        { text: this.funcaoService.obterNomePorChave(new GlobalService().obterSituacaoPatrimonio(), item.situacao), fontSize: 8, alignment: 'center', border: [true, false, true, true] },
        { text: `${item.empenho?.subelemento?.codigo || ''} ${item.empenho?.subelemento?.codigo ? '-' : ''} ${item.empenho?.subelemento?.nome || ''}`, fontSize: 8, border: [true, false, true, true] },
        { text: this.funcaoService.converteDataBR(item.data_cadastro), alignment: item.setor?.unidade ? 'left' : 'center', fontSize: 8, border: [true, false, true, true] },
        { text: this.funcaoService.converteDataBR(item.data_aquisicao), fontSize: 8, alignment: 'center', border: [true, false, true, true] },
        { text: this.funcaoService.convertToBrNumber(item.valor_aquisicao), fontSize: 8, alignment: 'center', border: [true, false, true, true] },
        { text: this.funcaoService.convertToBrNumber(item.valor_atual), fontSize: 8, alignment: 'center', border: [true, false, true, true] },
      ]);
    }

    cabecalho.push([
      { text: `Total de Itens: ${total_itens}`, fontSize: 10, colSpan: 7, border: [false, false, false, false] }, '', '', '', '', '', '',
      { text: `Valor Total:`, fontSize: 10, alignment: 'right', bold: true, border: [false, false, false, false] },
      { text: `${this.funcaoService.convertToBrNumber(valor_total)} `, fontSize: 10, alignment: 'center', bold: true, border: [false, false, false, false] },
    ]);

    assinatura.push(
      [
        { text: '', border: [false, false, false, false] },
        { text: '__________________________________________', alignment: 'center', border: [false, false, false, false], margin: [0, 30, 0, 0] },
        { text: '', border: [false, false, false, false] },
      ],
    );

    assinatura.push(
      [
        { text: '', border: [false, false, false, false] },
        { text: this.responsavel?.nome, alignment: 'center', border: [false, false, false, false] },
        { text: '', border: [false, false, false, false] },
      ],
    );

    conteudo.push([{
      layout: 'linhas',
      table: {
        dontBreakRows: true,
        widths: ['*', 'auto', '*', '*', 'auto', '*', '*', '*', '*'],
        body: cabecalho
      }
    }]);

    conteudo.push([{
      layout: 'linhas',
      table: {
        dontBreakRows: true,
        widths: ['*', 'auto', '*'],
        body: assinatura
      }
    }]);

    return conteudo;
  }

}
